import { useEffect, useState, useCallback } from 'react';
import {
    AdMob,
    BannerAdOptions,
    BannerAdSize,
    BannerAdPosition,
    BannerAdPluginEvents,
    AdMobBannerSize
} from '@capacitor-community/admob';

type DeviceType = 'desktop' | 'mobile';

interface UseAdsProps {
    desktopBreakpoint?: number;
    adsenseClientId?: string;
    admobBannerId?: string;
    isTesting?: boolean;
}

interface AdState {
    deviceType: DeviceType;
    isAdLoaded: boolean;
    error: string | null;
}

const DEFAULT_CONFIG = {
    desktopBreakpoint: 768,
    adsenseClientId: import.meta.env.VITE_PUBLIC_ADSENSE_CLIENT_ID,
    admobBannerId: import.meta.env.VITE_PUBLIC_ADMOB_BANNER_ID || 'ca-app-pub-3940256099942544/6300978111',
    isTesting: process.env.NODE_ENV === 'development'
} as const;

// Hook principal
const useAds = (props: UseAdsProps = {}) => {
    const config = { ...DEFAULT_CONFIG, ...props };

    // Estado
    const [adState, setAdState] = useState<AdState>(() => ({
        deviceType: typeof window !== 'undefined'
            ? window.innerWidth > config.desktopBreakpoint ? 'desktop' : 'mobile'
            : 'desktop',
        isAdLoaded: false,
        error: null
    }));

    // Helpers para actualizar estado
    const updateAdState = useCallback((updates: Partial<AdState>) => {
        setAdState(current => ({ ...current, ...updates }));
    }, []);

    const handleError = useCallback((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Error en anuncios';
        updateAdState({ error: errorMessage, isAdLoaded: false });
        console.error('Error en anuncios:', error);
    }, [updateAdState]);

    // Manejadores de AdMob
    const setupAdMobListeners = useCallback(() => {
        const listeners = [
            {
                event: BannerAdPluginEvents.Loaded,
                handler: () => updateAdState({ isAdLoaded: true, error: null })
            },
            {
                event: BannerAdPluginEvents.SizeChanged,
                handler: (size: AdMobBannerSize) => console.log('Banner size changed:', size)
            }
        ];

        // Registrar listeners
        listeners.forEach(({ event, handler }: any) => AdMob.addListener(event, handler));

    }, [updateAdState]);

    // Inicializadores de anuncios
    const initializeMobileAds = useCallback(async () => {
        try {
            await AdMob.initialize({
                testingDevices: config.isTesting ? ['EMULATOR'] : [],
                initializeForTesting: config.isTesting,
            });

            const options: BannerAdOptions = {
                adId: config.admobBannerId,
                adSize: BannerAdSize.BANNER,
                position: BannerAdPosition.BOTTOM_CENTER,
                margin: 0,
                isTesting: config.isTesting,
            };

            await AdMob.showBanner(options);
            setupAdMobListeners();
        } catch (error) {
            handleError(error);
        }
    }, [config.admobBannerId, config.isTesting, handleError, setupAdMobListeners]);

    const initializeDesktopAds = useCallback(() => {
        try {
            if (!config.adsenseClientId) return;

            (window as any).adsbygoogle = (window as any).adsbygoogle || [];
            (window as any).adsbygoogle.push({
                google_ad_client: config.adsenseClientId,
                enable_page_level_ads: true
            });

            updateAdState({ isAdLoaded: true, error: null });
        } catch (error) {
            handleError(error);
        }
    }, [config.adsenseClientId, updateAdState, handleError]);

    // Efecto para manejar el resize
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleResize = () => {
            const newDeviceType = window.innerWidth > config.desktopBreakpoint ? 'desktop' : 'mobile';

            if (newDeviceType !== adState.deviceType) {
                updateAdState({ deviceType: newDeviceType });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [config.desktopBreakpoint, adState.deviceType, updateAdState]);

    // Efecto para inicializar los anuncios
    useEffect(() => {
        const initializeAds = async () => {
            if (adState.deviceType === 'mobile') {
                await initializeMobileAds();
            } else {
                AdMob.hideBanner();
                initializeDesktopAds();
            }
        };

        initializeAds();

        return () => {
            if (adState.deviceType === 'mobile') {
                AdMob.hideBanner();
                AdMob.removeBanner();
            }
        };
    }, [adState.deviceType, initializeMobileAds, initializeDesktopAds]);

    return {
        ...adState,
        isDesktop: adState.deviceType === 'desktop',
        isMobile: adState.deviceType === 'mobile'
    };
};

export default useAds;